import { ButtonHTMLAttributes, ReactNode } from 'react';
import { LucideIcon, Loader2 } from 'lucide-react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: LucideIcon;
  children: ReactNode;
  fullWidth?: boolean;
}

/**
 * Button Component - Componente de botão seguindo design TAIS
 *
 * @example
 * ```tsx
 * <Button variant="primary" loading={isLoading}>
 *   Entrar
 * </Button>
 * ```
 */
export const Button = ({
  children,
  variant = 'primary',
  size = 'lg',
  loading = false,
  icon: Icon,
  fullWidth = false,
  className = '',
  disabled,
  ...props
}: ButtonProps) => {
  const baseStyles = `
    rounded-lg font-semibold
    transition-all duration-200
    flex items-center justify-center gap-2
    disabled:opacity-50 disabled:cursor-not-allowed
    active:scale-[0.98]
  `;

  const variants = {
    primary: `
      bg-[#F4CC04] text-black
      hover:bg-[#e5bf04]
      shadow-sm hover:shadow-md
      disabled:hover:bg-[#F4CC04]
    `,
    secondary: `
      bg-[#1C1C1C] text-white border border-[#333333]
      hover:bg-[#2A2A2A] hover:border-[#F4CC04]
      disabled:hover:bg-[#1C1C1C] disabled:hover:border-[#333333]
    `,
    ghost: `
      bg-transparent text-[#CCCCCC]
      hover:text-[#F4CC04] hover:bg-[#2A2A2A]
      disabled:hover:bg-transparent disabled:hover:text-[#CCCCCC]
    `,
  };

  const sizes = {
    sm: 'h-10 px-4 text-sm',
    md: 'h-11 px-6 text-base',
    lg: 'h-12 px-8 text-base',
  };

  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`}
      disabled={disabled || loading}
      aria-busy={loading}
      aria-live={loading ? 'polite' : undefined}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
          <span className="sr-only">Carregando...</span>
        </>
      ) : Icon ? (
        <Icon className="w-5 h-5" aria-hidden="true" />
      ) : null}
      {children}
    </button>
  );
};
